/**
 * The sites a neighborhood page will actually list.
 *
 * Airtable gives us two ways to answer this and they disagree: the
 * neighborhood's own `Sites` field (a list of site names) and each site's
 * `OHC Neighborhood` link. The second is larger — 190 sites against 174 — so
 * something upstream filters the neighborhood rollup. The card's count and the
 * page's list therefore have to come from the same place, or they contradict
 * each other on screen.
 *
 * `Confirmed Sites Count`, which the card used to print, is a third answer
 * again: it is 0 on all 27 neighborhoods, which is why every card read
 * "0 Sites" while the pages behind them listed sites fine (Asana O2-40).
 *
 * Ordering follows the neighborhood's list, which is what the page did before.
 */
export const neighborhoodSiteRecords = (neighborhood: any, sites: any[]): any[] => {
    const names = neighborhood?.fields?.Sites || [];
    if (!names.length || !sites?.length) return [];

    const byName = new Map<string, any>();
    for (const site of sites) {
        const name = site?.fields?.Site;
        if (name && !byName.has(name)) byName.set(name, site);
    }

    return names.map((name: string) => byName.get(name)).filter(Boolean);
};

/**
 * The itineraries worth showing on a neighborhood page — Asana O2-60.
 *
 * Matched through the SITES, not through the itinerary's own `OHC Neighborhood`
 * link, which the page used to filter on. That field is populated on 2 of 16
 * itineraries, and the filter also only compared its FIRST entry, so the
 * "Itineraries in this Area" block almost never appeared — including on Near
 * West Side, which has six itineraries running through it.
 *
 * The two sides do not share a key, which is what hid this: a neighborhood's
 * `Sites` field holds site NAMES, while an itinerary's `Organizations` holds
 * site RECORD IDS. Resolving the names to records first is what makes them
 * comparable, and neighborhoodSiteRecords already does exactly that.
 *
 * Order follows the itinerary list as given.
 */
export const itinerariesForNeighborhood = (
    neighborhood: any,
    sites: any[],
    itineraries: any[],
): any[] => {
    const ids = new Set(neighborhoodSiteRecords(neighborhood, sites).map((s) => s?.id).filter(Boolean));
    if (!ids.size || !itineraries?.length) return [];

    return itineraries.filter((itinerary) => {
        const orgs = itinerary?.fields?.Organizations;
        return Array.isArray(orgs) && orgs.some((id: string) => ids.has(id));
    });
};
